import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, Shield, Bell, Home, CalendarDays, Trophy, Gift } from "lucide-react";
import Logo from "@/components/Logo";
import { useApp } from "@/context/AppContext";

const LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/come-funziona", label: "Come Funziona" },
  { to: "/calendario", label: "Calendario" },
  { to: "/classifica", label: "Classifica" },
  { to: "/premi", label: "Premi" },
  { to: "/faq", label: "FAQ" },
];

export default function Navbar() {
  const { user, logout, notifications } = useApp();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const unread = (notifications || []).filter((n) => !n.read).length;

  const doLogout = () => { logout(); setOpen(false); navigate("/"); };

  return (
    <header className="sticky top-0 z-50 bg-ink-900/90 backdrop-blur-xl border-b border-gold-300/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" onClick={() => setOpen(false)}><Logo /></Link>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) => `nav-link text-sm ${isActive ? "active" : ""}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              {user.role === "superadmin" && (
                <Link to="/admin" data-testid="nav-admin-btn" className="btn-outline-gold !py-2 !px-4 text-sm"><Shield size={16} /> Admin</Link>
              )}
              <Link to="/profilo" data-testid="nav-profile-btn" className="relative inline-flex items-center gap-2 text-slate-200 hover:text-gold-300 transition">
                <User size={20} />
                {unread > 0 && <span className="absolute -top-2 -right-2 bg-gold-300 text-ink-900 text-[0.6rem] font-bold rounded-full h-4 min-w-4 px-1 grid place-items-center">{unread}</span>}
              </Link>
              <button onClick={doLogout} data-testid="nav-logout-btn" className="text-slate-400 hover:text-white transition" title="Esci"><LogOut size={18} /></button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-btn" className="btn-outline-gold !py-2 !px-5 text-sm">Accedi</Link>
              <Link to="/abbonamento" data-testid="nav-subscribe-btn" className="btn-gold !py-2 !px-5 text-sm">Abbonati · 4,99€</Link>
            </>
          )}
        </div>

        <button className="lg:hidden text-white" onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-ink-900 border-t border-gold-300/15 px-4 py-4 space-y-1 animate-fade-up">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
              className={({ isActive }) => `block py-2.5 nav-link ${isActive ? "active" : ""}`}>{l.label}</NavLink>
          ))}
          <div className="pt-3 border-t border-gold-300/15 flex flex-col gap-2">
            {user ? (
              <>
                {user.role === "superadmin" && <Link to="/admin" onClick={() => setOpen(false)} className="btn-outline-gold"><Shield size={16} /> Pannello Admin</Link>}
                <Link to="/profilo" onClick={() => setOpen(false)} className="btn-outline-gold"><User size={16} /> Il mio profilo</Link>
                <button onClick={doLogout} className="btn-outline-gold"><LogOut size={16} /> Esci</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="btn-outline-gold">Accedi</Link>
                <Link to="/abbonamento" onClick={() => setOpen(false)} className="btn-gold">Abbonati · 4,99€</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export function MobileBottomBar() {
  const items = [
    { to: "/", icon: Home, label: "Home", end: true },
    { to: "/calendario", icon: CalendarDays, label: "Partite" },
    { to: "/classifica", icon: Trophy, label: "Classifica" },
    { to: "/premi", icon: Gift, label: "Premi" },
    { to: "/profilo", icon: User, label: "Profilo" },
  ];
  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-ink-900/95 backdrop-blur-lg border-t border-gold-300/20 flex justify-around items-center py-2">
      {items.map(({ to, icon: Icon, label, end }) => (
        <NavLink key={to} to={to} end={end} data-testid={`bottom-${label.toLowerCase()}`}
          className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2 ${isActive ? "text-gold-300" : "text-slate-500"}`}>
          <Icon size={20} />
          <span className="text-[0.6rem]">{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}
